import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import Iconify from '../iconify';
import { responsiveModalPaperSx, codeDigitFieldSx } from './modalLayout';
import {
  setupRecaptcha,
  sendPhoneOTP,
  verifyPhoneOTP,
  checkRateLimit,
  getOtpRemainingTime,
  isOtpExpired,
  formatPhoneForDisplay,
  clearPhoneVerificationData,
} from '../../service/phoneVerification';
import { isLocalhost, firebase } from '../../config/firebase';
import '../../styles/phone-input.scss';

const OTP_LENGTH = 6;
const SUCCESS_REDIRECT_DELAY = 2000;

export default function PhoneVerificationModal({
  open,
  handleClose,
  onVerificationComplete,
  userData,
}) {
  const [step, setStep] = React.useState('phone'); // 'phone' | 'otp' | 'success'
  const [loading, setLoading] = React.useState(false);
  const [confirmationResult, setConfirmationResult] = React.useState(null);
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [otpTimer, setOtpTimer] = React.useState(0);
  const [rateLimit, setRateLimit] = React.useState({ allowed: true, remainingAttempts: 3 });
  const [error, setError] = React.useState('');
  const [recaptchaSolved, setRecaptchaSolved] = React.useState(false);
  const [gdprConsent, setGdprConsent] = React.useState(false);
  const [phoneError, setPhoneError] = React.useState('');

  // OTP input refs and values
  const [otpValues, setOtpValues] = React.useState(Array(OTP_LENGTH).fill(''));
  const otpInputRefs = React.useRef([]);

  // Initialize refs array
  React.useEffect(() => {
    otpInputRefs.current = otpInputRefs.current.slice(0, OTP_LENGTH);
  }, []);

  // reCAPTCHA setup:
  // - Localhost: skip entirely (appVerificationDisabledForTesting handles it; verifier created on send)
  // - Production: visible "I'm not a robot" widget inside the modal
  const initRecaptcha = React.useCallback(() => {
    if (isLocalhost) {
      // Localhost: no reCAPTCHA widget needed, verifier is created fresh on each Send
      setRecaptchaSolved(true);
      return;
    }
    // Production: visible reCAPTCHA mounted on document.body (not inside
    // this modal — overflow + focus trap is why SMS never left Firebase
    // on phones).
    setRecaptchaSolved(false);
    setTimeout(() => {
      setupRecaptcha(undefined, {
        onSolved: () => setRecaptchaSolved(true),
        onExpired: () => setRecaptchaSolved(false),
      }).catch((err) => {
        console.error('reCAPTCHA setup failed:', err);
        setRecaptchaSolved(false);
      });
    }, 300);
  }, []);

  React.useEffect(() => {
    if (!open || step !== 'phone') return;
    initRecaptcha();
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          // ignore
        }
        window.recaptchaVerifier = null;
      }
    };
  }, [open, step, initRecaptcha]);

  // OTP countdown timer
  React.useEffect(() => {
    let interval;
    if (step === 'otp' && !isOtpExpired()) {
      interval = setInterval(() => {
        const remaining = getOtpRemainingTime();
        setOtpTimer(remaining);
        if (remaining <= 0) clearInterval(interval);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step]);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setRateLimit(checkRateLimit());
      setStep('phone');
      setError('');
      setRecaptchaSolved(false);
      setPhoneNumber('');
      setOtpValues(Array(OTP_LENGTH).fill(''));
      setGdprConsent(false);
      setPhoneError('');
    }
  }, [open]);

  // Cleanup on unmount
  React.useEffect(() => () => clearPhoneVerificationData(), []);

  // Focus first OTP input when step changes to OTP
  React.useEffect(() => {
    if (step === 'otp' && otpInputRefs.current[0]) {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // Success redirect timer
  React.useEffect(() => {
    if (step === 'success') {
      const timer = setTimeout(() => {
        onVerificationComplete({ ...userData, phoneNumber, phoneVerified: true });
        handleClose();
        setStep('phone');
        setPhoneNumber('');
        setConfirmationResult(null);
        setOtpValues(Array(OTP_LENGTH).fill(''));
      }, SUCCESS_REDIRECT_DELAY);
      return () => clearTimeout(timer);
    }
  }, [step, userData, phoneNumber, onVerificationComplete, handleClose]);

  const handleSendOTP = async () => {
    // Validate phone number
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid phone number');
      return;
    }
    setPhoneError('');

    if (!gdprConsent) {
      setError('Please agree to the Privacy Policy');
      return;
    }

    setLoading(true);
    setError('');

    let verifier;

    if (isLocalhost) {
      // Localhost: create a fresh invisible verifier each time in document.body
      // (appVerificationDisabledForTesting auto-solves it for test phone numbers)
      try {
        // Clean up any previous verifier
        if (window.recaptchaVerifier) {
          try { window.recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
          window.recaptchaVerifier = null;
        }
        const oldContainer = document.getElementById('recaptcha-localhost');
        if (oldContainer) oldContainer.remove();

        const container = document.createElement('div');
        container.id = 'recaptcha-localhost';
        document.body.appendChild(container);

        verifier = new firebase.auth.RecaptchaVerifier('recaptcha-localhost', {
          size: 'invisible',
        });
        await verifier.render();
      } catch (e) {
        console.error('Localhost verifier setup failed:', e);
        setError('Security check setup failed. Please refresh the page.');
        setLoading(false);
        return;
      }
    } else {
      // Production: use the visible reCAPTCHA verifier
      verifier = window.recaptchaVerifier;
      if (!verifier) {
        setError('Please complete the "I\'m not a robot" check first.');
        setLoading(false);
        return;
      }
    }

    const result = await sendPhoneOTP(phoneNumber, verifier);
    if (result.alreadyLinked) {
      setStep('success');
    } else if (result.success) {
      setConfirmationResult(result.confirmationResult);
      setStep('otp');
      setOtpTimer(300);
      setRateLimit(checkRateLimit());
    } else if (result.rateLimited) {
      setRateLimit({ allowed: false, remainingAttempts: 0, waitTime: result.waitTime });
    } else {
      setError(result.message || 'Failed to send code');
      // Production: re-create reCAPTCHA so user can retry
      if (!isLocalhost) {
        initRecaptcha();
      }
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    const otp = otpValues.join('');
    if (otp.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await verifyPhoneOTP(confirmationResult, otp);
    if (result.success) {
      setStep('success');
    } else {
      setError(result.expired ? 'Code expired. Please request a new one.' : 'Invalid code. Please try again.');
      if (result.expired) setOtpTimer(0);
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    const rateLimitStatus = checkRateLimit();
    if (!rateLimitStatus.allowed) {
      setRateLimit(rateLimitStatus);
      setLoading(false);
      return;
    }
    // Invisible challenge, mounted on document.body (not inside this
    // modal). disableEnforceFocus above is what lets the iframe receive
    // the tap on phones.
    let verifier;
    try {
      verifier = await setupRecaptcha(undefined, { invisible: true });
    } catch (e) {
      setError('Security check failed to load. Please try again.');
      setLoading(false);
      return;
    }
    const result = await sendPhoneOTP(phoneNumber, verifier);
    if (result.alreadyLinked) {
      setStep('success');
    } else if (result.success) {
      setConfirmationResult(result.confirmationResult);
      setOtpTimer(300);
      setRateLimit(checkRateLimit());
      setOtpValues(Array(OTP_LENGTH).fill(''));
      setError('');
      otpInputRefs.current[0]?.focus();
    } else if (result.rateLimited) {
      setRateLimit({ allowed: false, remainingAttempts: 0, waitTime: result.waitTime });
    } else {
      setError(result.message || 'Failed to resend code');
    }
    setLoading(false);
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newOtpValues = [...otpValues];
    newOtpValues[index] = digit;
    setOtpValues(newOtpValues);

    // Auto-advance to next input
    if (digit && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP keydown (backspace, arrows)
  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      if (!otpValues[index] && index > 0) {
        // Move to previous input if current is empty
        otpInputRefs.current[index - 1]?.focus();
        const newOtpValues = [...otpValues];
        newOtpValues[index - 1] = '';
        setOtpValues(newOtpValues);
      } else {
        // Clear current input
        const newOtpValues = [...otpValues];
        newOtpValues[index] = '';
        setOtpValues(newOtpValues);
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    
    if (pastedData.length > 0) {
      const newOtpValues = Array(OTP_LENGTH).fill('');
      for (let i = 0; i < pastedData.length && i < OTP_LENGTH; i++) {
        newOtpValues[i] = pastedData[i];
      }
      setOtpValues(newOtpValues);
      
      // Focus appropriate input
      const nextEmptyIndex = newOtpValues.findIndex((v) => !v);
      if (nextEmptyIndex !== -1) {
        otpInputRefs.current[nextEmptyIndex]?.focus();
      } else {
        otpInputRefs.current[OTP_LENGTH - 1]?.focus();
      }
    }
  };

  const formatTime = (seconds) =>
    `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;

  const handleBack = () => {
    if (step === 'otp') {
      setStep('phone');
      setOtpValues(Array(OTP_LENGTH).fill(''));
      setError('');
    } else {
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      disableAutoFocus
      disableEnforceFocus
      aria-labelledby="phone-verification-title"
      aria-describedby="phone-verification-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="phone-verification-title"
        data-testid="phone-verification-modal"
        sx={responsiveModalPaperSx}
      >
        {/* Loading bar */}
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              '& .MuiLinearProgress-bar': { bgcolor: '#242258' },
            }}
          />
        )}

        {/* Success Step */}
        {step === 'success' ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Box
              className="success-pulse"
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#D1FAE5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Iconify
                icon="eva:checkmark-circle-2-fill"
                width={48}
                sx={{ color: '#10B981' }}
                className="success-checkmark"
              />
            </Box>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#111827',
                mb: 1,
              }}
            >
              Phone Number Verified!
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
              Redirecting you to the dashboard...
            </Typography>
            <CircularProgress
              size={20}
              sx={{ mt: 3, color: '#242258' }}
            />
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <IconButton
                onClick={handleBack}
                aria-label={step === 'otp' ? 'Go back to phone number entry' : 'Close dialog'}
                data-testid="phone-modal-back"
                sx={{
                  width: 36,
                  height: 36,
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  '&:hover': { bgcolor: '#F9FAFB' },
                }}
              >
                <Iconify icon="eva:arrow-back-fill" width={16} sx={{ color: '#374151' }} />
              </IconButton>
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: 'center', pb: 2 }}>
              <Typography
                id="phone-verification-title"
                sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}
              >
                {step === 'phone' ? 'Verify Your Phone' : 'Enter Verification Code'}
              </Typography>
              <Typography
                id="phone-verification-description"
                sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5 }}
              >
                {step === 'phone'
                  ? "We'll send you a verification code via SMS"
                  : `Code sent to ${formatPhoneForDisplay(phoneNumber)}`}
              </Typography>
            </Box>

            <Box sx={{ px: 3, pb: 3 }}>
              {/* Error message */}
              {error && (
                <Box
                  role="alert"
                  aria-live="assertive"
                  sx={{
                    py: 1,
                    px: 1.5,
                    mb: 2,
                    bgcolor: '#FEF2F2',
                    borderRadius: '12px',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '13px', color: '#DC2626', fontWeight: 500 }}>
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Phone Step */}
              {step === 'phone' && (
                <>
                  <Typography
                    sx={{ fontSize: '13px', color: '#6B7280', mb: 1.5, textAlign: 'center' }}
                  >
                    {isLocalhost
                      ? 'Enter your phone number, agree to the terms, and click Send Code.'
                      : 'Complete the security check below, enter your phone number, and agree to the terms. Then click Send Code.'}
                  </Typography>

                  {/* Phone Input */}
                  <Typography
                    component="label"
                    htmlFor="phone-input"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#374151',
                      mb: 0.5,
                      display: 'block',
                    }}
                  >
                    Phone Number
                  </Typography>
                  <Box
                    className={`phone-input-container ${phoneError ? 'phone-input-error' : ''}`}
                    sx={{ mb: 0.5 }}
                  >
                    <PhoneInput
                      id="phone-input"
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="US"
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      placeholder="Enter phone number"
                      data-testid="phone-input"
                    />
                  </Box>
                  {phoneError && (
                    <Typography sx={{ fontSize: '12px', color: '#DC2626', mb: 1 }}>
                      {phoneError}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mb: 1.5 }}>
                    Select your country and enter your phone number
                  </Typography>

                  {/* reCAPTCHA lives on document.body (see setupRecaptcha).
                      A spacer keeps the Send button from sitting under it. */}
                  {!isLocalhost && (
                    <Typography sx={{ fontSize: '12px', color: '#6B7280', mb: 1.5, textAlign: 'center' }}>
                      Complete the "I'm not a robot" check — it appears at the bottom of the screen.
                    </Typography>
                  )}

                  {/* GDPR Consent */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Checkbox
                      id="gdprConsent"
                      checked={gdprConsent}
                      onChange={(e) => setGdprConsent(e.target.checked)}
                      size="small"
                      aria-label="I agree to the Privacy Policy"
                      data-testid="gdpr-consent"
                      sx={{ p: 0, mr: 1, mt: 0.25, '&.Mui-checked': { color: '#242258' } }}
                    />
                    <Typography
                      component="label"
                      htmlFor="gdprConsent"
                      sx={{ fontSize: '12px', color: '#6B7280', cursor: 'pointer', lineHeight: 1.5 }}
                    >
                      I agree to receive SMS messages and accept the{' '}
                      <Box
                        component="a"
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#242258', fontWeight: 600, textDecoration: 'none' }}
                      >
                        Privacy Policy
                      </Box>
                      . Standard SMS rates may apply.
                    </Typography>
                  </Box>

                  {/* Rate limit warning */}
                  {!rateLimit.allowed && (
                    <Box
                      role="alert"
                      aria-live="polite"
                      sx={{
                        py: 1,
                        px: 1.5,
                        mb: 2,
                        bgcolor: '#FFFBEB',
                        borderRadius: '12px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#D97706' }}>
                        Too many attempts. Please wait {Math.ceil(rateLimit.waitTime / 60000)} minutes.
                      </Typography>
                    </Box>
                  )}

                  <Button
                    onClick={handleSendOTP}
                    disabled={loading || !rateLimit.allowed || !gdprConsent || !recaptchaSolved}
                    fullWidth
                    data-testid="send-code-btn"
                    sx={{
                      height: 46,
                      bgcolor: '#242258',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1a1a45' },
                      '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send Code'}
                  </Button>

                  {isLocalhost && (
                    <Box
                      sx={{
                        mt: 1.5,
                        py: 1,
                        px: 1.5,
                        bgcolor: '#EFF6FF',
                        borderRadius: '8px',
                        textAlign: 'center',
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', color: '#2563EB', fontWeight: 500 }}>
                        Dev mode: Use a test phone number from Firebase Console
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#6B7280' }}>
                        e.g. +1 123 456 7890 &rarr; code: 123456
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* OTP Step */}
              {step === 'otp' && (
                <>
                  {/* 6 OTP Input Boxes */}
                  <Box
                    className="otp-input-container"
                    sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 } }}
                    onPaste={handleOtpPaste}
                  >
                    {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (otpInputRefs.current[index] = el)}
                        value={otpValues[index]}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className={`otp-input-box ${otpValues[index] ? 'otp-filled' : ''}`}
                        inputProps={{
                          maxLength: 1,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          'aria-label': `Digit ${index + 1} of ${OTP_LENGTH}`,
                          'data-testid': `otp-input-${index}`,
                          style: {
                            textAlign: 'center',
                            fontSize: '24px',
                            fontWeight: 600,
                          },
                        }}
                        sx={{
                          ...codeDigitFieldSx,
                          '& .MuiOutlinedInput-root': {
                            ...codeDigitFieldSx['& .MuiOutlinedInput-root'],
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                            '&.Mui-focused fieldset': { borderColor: '#242258', borderWidth: 2 },
                          },
                        }}
                      />
                    ))}
                  </Box>

                  {/* Timer and Resend */}
                  {otpTimer > 0 ? (
                    <Typography
                      id="otp-timer"
                      aria-live="polite"
                      sx={{ fontSize: '13px', color: '#6B7280', textAlign: 'center', mb: 2 }}
                    >
                      Code expires in{' '}
                      <Box component="span" sx={{ fontWeight: 600, color: '#111827' }}>
                        {formatTime(otpTimer)}
                      </Box>
                    </Typography>
                  ) : (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography sx={{ fontSize: '13px', color: '#DC2626', fontWeight: 500, mb: 1 }}>
                        Code expired
                      </Typography>
                      <Button
                        onClick={handleResendOTP}
                        disabled={loading || !rateLimit.allowed}
                        aria-label="Resend verification code"
                        data-testid="resend-code-btn"
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#242258',
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#F3F4F6' },
                          '&:disabled': { color: '#9CA3AF' },
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={16} sx={{ color: '#242258', mr: 1 }} />
                        ) : (
                          <Iconify icon="eva:refresh-outline" width={16} sx={{ mr: 0.5 }} />
                        )}
                        Resend Code
                      </Button>
                      {!rateLimit.allowed && (
                        <Typography sx={{ fontSize: '11px', color: '#D97706', mt: 0.5 }}>
                          Wait {Math.ceil((rateLimit.waitTime || 0) / 60000)} min
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Attempts remaining */}
                  {rateLimit.remainingAttempts < 3 && rateLimit.remainingAttempts > 0 && (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#D97706',
                        textAlign: 'center',
                        mb: 2,
                      }}
                    >
                      {rateLimit.remainingAttempts} attempt{rateLimit.remainingAttempts !== 1 ? 's' : ''} remaining
                    </Typography>
                  )}

                  <Button
                    onClick={handleVerifyOTP}
                    disabled={loading || otpValues.some((v) => !v)}
                    fullWidth
                    data-testid="verify-btn"
                    sx={{
                      height: 46,
                      bgcolor: '#242258',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1a1a45' },
                      '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Verify'}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
